import { useMemo } from 'react';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import { isJsmPlaygroundSignup } from '@/components/signup/utils/jsm-playground-signup-intent';
import { ProductKey } from '@/components/signup/utils/products';
import { experiments } from '@/utils/experiment/manifest';
import { useExperiment } from '@/utils/experiment/use-experiment';

import { PerceivedProvisioningStartTimer } from '../../utils/perceived-provisioning/perceived-provisioning-start-timer';
import { defaultScreenConfig } from '../../utils/screen-manager/default-screen-config';
import { teamChoiceDictionaryItem } from '../../utils/screen-manager/default-screen-dictionary';
import {
	JSM_USE_CASE_PROVISIONING,
	JSM_USE_CASE_TEMPLATE_CHOICE,
	PROVISIONING,
	TEAM_CHOICE,
} from '../../utils/screen-manager/utils';
import { JsmUseCaseProvisioningScreen } from '../jsm-use-case-provisioning/jsm-use-case-provisioning-screen';
import { getJsmRecommendedTemplateId } from '../jsm-use-case-template-choice/jsm-use-case-template-choice-constants';
import { JsmUseCaseTemplateChoiceScreen } from '../jsm-use-case-template-choice/jsm-use-case-template-choice-screen';
import { ProductRedirector } from '../product-redirector/product-redirector';
import ServiceCollectionProvisioningScreen from '../service-collection-provisioning-screen/service-collection-provisioning-screen-lofi';

import { BaseProvisioningUI } from './base-provisioning-ui';

const serviceCollectionScreenDictionary = {
	...teamChoiceDictionaryItem,
	[PROVISIONING]: (
		<PerceivedProvisioningStartTimer>
			<ProductRedirector>
				<ServiceCollectionProvisioningScreen />
			</ProductRedirector>
		</PerceivedProvisioningStartTimer>
	),
};

const noop = () => undefined;

const jsmUseCaseTemplateChoiceConfig = {
	[JSM_USE_CASE_TEMPLATE_CHOICE]: {
		position: { default: 200 },
		visibility: { default: true },
		onMount: noop,
	},
	[JSM_USE_CASE_PROVISIONING]: {
		position: { default: 300 },
		visibility: { default: true },
		onMount: noop,
	},
};

const jsmUseCaseServiceCollectionScreenDictionary = {
	...teamChoiceDictionaryItem,
	[JSM_USE_CASE_TEMPLATE_CHOICE]: <JsmUseCaseTemplateChoiceScreen />,
	[JSM_USE_CASE_PROVISIONING]: <JsmUseCaseProvisioningScreen />,
};

const playgroundServiceCollectionScreenDictionary = {
	...teamChoiceDictionaryItem,
	[JSM_USE_CASE_PROVISIONING]: <JsmUseCaseProvisioningScreen />,
};

/**
 * Component that shows Service Collection provisioning UI with team choice support.
 * @returns @type {JSX.Element}
 */
export const ServiceCollectionProvisioningUI = () => {
	const { products = [], primaryCollectionProduct, teamType, templateId } = useSearchParameters();
	const shouldUsePlaygroundFlow = useMemo(
		() =>
			isJsmPlaygroundSignup(window.location.search) &&
			products.includes(ProductKey.SERVICE_COLLECTION),
		[products],
	);
	const hasConfirmedPlaygroundSelection =
		Boolean(templateId) && getJsmRecommendedTemplateId(teamType) === templateId;
	const jsmGrowth519Result = useExperiment(experiments.jsmGrowth519JsmLandUseCaseOnboarding);
	const isJsmExperimentEnabled =
		jsmGrowth519Result.type === 'enrolled'
			? jsmGrowth519Result.value
			: experiments.jsmGrowth519JsmLandUseCaseOnboarding.defaultValue;
	const shouldUseJsmUseCaseTemplateChoice =
		isJsmExperimentEnabled &&
		products.includes(ProductKey.SERVICE_COLLECTION) &&
		primaryCollectionProduct === ProductKey.JIRA_SERVICE_MANAGEMENT;

	const screenConfig = useMemo(() => {
		if (shouldUsePlaygroundFlow) {
			return {
				[TEAM_CHOICE]: {
					position: { default: 100 },
					visibility: { default: !hasConfirmedPlaygroundSelection },
					onMount: noop,
				},
				[JSM_USE_CASE_PROVISIONING]: {
					position: { default: 200 },
					visibility: { default: true },
					onMount: noop,
				},
			};
		}

		return shouldUseJsmUseCaseTemplateChoice
			? {
					...defaultScreenConfig,
					...jsmUseCaseTemplateChoiceConfig,
				}
			: defaultScreenConfig;
	}, [hasConfirmedPlaygroundSelection, shouldUseJsmUseCaseTemplateChoice, shouldUsePlaygroundFlow]);

	const screenDictionary = useMemo(() => {
		if (shouldUsePlaygroundFlow) {
			return playgroundServiceCollectionScreenDictionary;
		}

		return shouldUseJsmUseCaseTemplateChoice
			? jsmUseCaseServiceCollectionScreenDictionary
			: serviceCollectionScreenDictionary;
	}, [shouldUseJsmUseCaseTemplateChoice, shouldUsePlaygroundFlow]);

	return (
		<BaseProvisioningUI
			screenConfig={screenConfig}
			screenDictionary={screenDictionary}
			screenFallback={
				<PerceivedProvisioningStartTimer>
					<ProductRedirector>
						<ServiceCollectionProvisioningScreen />
					</ProductRedirector>
				</PerceivedProvisioningStartTimer>
			}
		/>
	);
};
